import React from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'ramda'
import { Row, Col, Spin } from 'antd'
import HighchartsReact from 'highcharts-react-official'
import * as Highcharts from 'highcharts'
import Box from '@shared/Box'

import { PAGE_SIZE, TYPE } from '@services/report'
import { initChart, tooltipHeaderFormat, tooltipPointFormat } from '../ReportChartConfig'
import ChartTitle from '../ChartTitle'
import Pagination from '../Pagination'
import type { ICommonProps } from '../type'

export function LinkRateChart(props: ICommonProps) {
  const { queryVariables, yAxisDateStr, dataSource, loading } = props
  const { t } = useTranslation()

  const [startIdx, setStartIdx] = React.useState(0)
  const [currentChart, setCurrentChart] = React.useState(0)
  const query = React.useMemo(() => queryVariables, [queryVariables])
  const [options, setOptions] = React.useState(initChart)

  const chart = React.useRef<HighchartsReact.RefObject>(null)

  React.useEffect(() => {
    if (isEmpty(dataSource.content)) {
      return
    }

    const endIdx = startIdx + PAGE_SIZE

    setOptions((x: any) => ({
      ...x,
      xAxis: {
        ...initChart.xAxis[0],
        categories: dataSource.xAxisLabels.slice(startIdx, endIdx),
      },
      yAxis: [
        { ...initChart.yAxis[0] },
        {
          ...initChart.yAxis[1],
          title: {
            ...initChart.yAxis[1].title,
            text: t('report:correctRate yAxis Label'),
          },
        },
      ],
      tooltip: {
        ...initChart.tooltip,
        formatter: function (): any {
          return (this as any).points.reduce(function (s: any, point: any) {
            return s + tooltipPointFormat(point, true, '%')
          }, tooltipHeaderFormat(this))
        },
      },
      series: [
        {
          name: t('report:correctRate'),
          type: 'column',
          yAxis: 1,
          data: dataSource.xAxis?.slice(startIdx, endIdx),
        },
        {
          name: t('report:avg correctRate'),
          type: 'line',
          data: dataSource.yAxis.slice(startIdx, endIdx),
          tooltip: {
            valueSuffix: '%',
          },
        },
      ],
    }))
  }, [dataSource, currentChart]) // eslint-disable-line

  const handleNextPage = React.useCallback(() => {
    const idx = currentChart + 1

    setStartIdx(PAGE_SIZE * idx)
    setCurrentChart(idx)
  }, [currentChart])

  const handlePrevPage = React.useCallback(() => {
    const idx = currentChart - 1

    setStartIdx(PAGE_SIZE * idx)
    setCurrentChart(idx)
  }, [currentChart])

  React.useEffect(() => {
    if (isEmpty(query)) {
      return
    }

    setStartIdx(0)
    setCurrentChart(0)
  }, [query]) // eslint-disable-line

  React.useEffect(() => {
    if (!loading) {
      chart?.current?.chart?.hideLoading()
      chart?.current?.chart?.setSize(null, null)
    } else {
      chart?.current?.chart?.showLoading()
    }
  }, [chart, loading])

  const { totalPages } = dataSource

  return (
    <>
      <Row gutter={[48, 48]} className="mr-40">
        <Col span={24}>
          <Pagination
            className="my-40 ml-[65px]"
            page={currentChart + 1}
            totalPages={totalPages}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
          />
        </Col>
        <Col span={24}>
          <Spin spinning={loading}>
            <Box position="relative">
              <Box
                position="absolute"
                top="-35px"
                left="65px"
                zIndex={1}
                fontSize="18px"
                className="font-medium">
                %
              </Box>

              <HighchartsReact
                ref={chart}
                highcharts={Highcharts}
                containerProps={{ style: { width: '100%', height: '100%' } }}
                options={options}
              />

              {query.reportType !== TYPE.DAY ? (
                <Box position="absolute" className="font-medium" right="0px" fontSize="14px">
                  {yAxisDateStr}
                </Box>
              ) : (
                t('report:days')
              )}
            </Box>
          </Spin>
        </Col>
        <ChartTitle {...props} />
      </Row>
    </>
  )
}

export default React.memo(LinkRateChart)
